import { createFileRoute, Link } from "@tanstack/react-router";

import { seo } from "@/lib/seo";
import { Section } from "@/components/Section";
import { Container } from "@/components/Container";
import { Eyebrow } from "@/components/Eyebrow";
import { Aurora } from "@/components/Aurora";
import { CtaButton } from "@/components/CtaButton";
import { PagePlaceholder } from "@/components/site/PagePlaceholder";
import { getCaseStudies } from "@/lib/content";

export const Route = createFileRoute("/_site/results/")({
  loader: async () => ({ caseStudies: await getCaseStudies({ data: {} }) }),
  head: () =>
    seo({
      title: "Client Results",
      description: "Engagement outcomes and the work behind them.",
      path: "/results",
    }),
  component: Page,
  errorComponent: () => (
    <PagePlaceholder title="This page didn't load" intro="Try refreshing the page." />
  ),
  notFoundComponent: () => (
    <PagePlaceholder title="Not found" intro="This page doesn't exist or was moved." />
  ),
});

function Page() {
  const { caseStudies } = Route.useLoaderData();

  return (
    <>
      <Section tone="paper" className="relative overflow-hidden">
        <Aurora variant="light" className="opacity-70" />
        <Container className="relative">
          <Eyebrow>Results</Eyebrow>
          <h1 className="mt-5 max-w-[18ch] font-display text-display-lg text-ink-900">
            The work, and what it did.
          </h1>
          <p className="mt-5 max-w-[60ch] text-body-lg text-ink-600">
            {caseStudies.length > 0
              ? "Engagements we can name, with the numbers behind them."
              : "We publish an engagement here once the client approves the details and the numbers are verified. Nothing is posted before then. Ask us on a call and we'll walk you through comparable work."}
          </p>
        </Container>
      </Section>

      {caseStudies.length > 0 && (
        <Section tone="raised">
          <Container>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {caseStudies.map((study) => (
                <article
                  key={study.id}
                  className="flex h-full flex-col rounded-2xl border border-ink-200 bg-paper-0 p-6 shadow-[0_1px_2px_oklch(0.1831_0.0309_263.38/0.05)] transition-brand hover:border-brand-600/40 md:p-8"
                >
                  {study.industry && (
                    <span className="inline-flex w-fit rounded-full bg-brand-100 px-3 py-1 font-mono text-eyebrow uppercase font-medium text-brand-800">
                      {study.industry}
                    </span>
                  )}
                  <h2 className="mt-5 font-display text-h3 text-ink-900">
                    {study.outcome_headline}
                  </h2>
                  {study.summary && (
                    <p className="mt-3 text-body text-ink-600">{study.summary}</p>
                  )}
                  <Link
                    to="/results/$slug"
                    params={{ slug: study.slug }}
                    className="mt-auto pt-6 text-body-sm font-medium text-brand-700 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700"
                  >
                    Read the full story
                  </Link>
                </article>
              ))}
            </div>
          </Container>
        </Section>
      )}

      <Section tone="dark">
        <Container>
          <div className="relative overflow-hidden rounded-3xl bg-ink-900 p-8 sm:p-12 lg:p-16">
            <Aurora variant="dark" />
            <div className="relative grid items-center gap-10 lg:grid-cols-[1fr_auto] lg:gap-16">
              <div className="max-w-[52ch]">
                <Eyebrow className="text-brand-300">Next step</Eyebrow>
                <h2 className="mt-3 font-display text-h2 text-paper-0" style={{ textWrap: "balance" }}>
                  Want results like these?
                </h2>
                <p className="mt-4 text-body-lg text-ondark-body">
                  Twenty minutes, no cost, no pitch. We'll tell you honestly whether AI or funding
                  support makes sense for your business.
                </p>
              </div>
              <div className="flex flex-col items-start gap-4 lg:items-center">
                <CtaButton asChild tone="dark">
                  <Link to="/book">Book a strategy call</Link>
                </CtaButton>
              </div>
            </div>
          </div>
        </Container>
      </Section>
    </>
  );
}
